import { camelCase } from "lodash-es";

import { type Color, Colors, type ColorValue } from "shared/types/colors";

export type ColorWithPrefix<Prefix extends string> = Extract<
  Color,
  `${Prefix}${string}`
>;

export type PrefixedColors<Prefix extends string> = {
  [K in ColorWithPrefix<Prefix>]: (typeof Colors)[K];
};

export type MenuColor = ColorWithPrefix<"menu-">;

export type SentimentColor = ColorWithPrefix<"sentiment-">;

function getTokensByPrefix<Prefix extends string>(
  prefix: Prefix
): PrefixedColors<Prefix> {
  const tokens = {} as PrefixedColors<Prefix>;

  (Object.entries(Colors) as [Color, ColorValue][]).forEach(([key, value]) => {
    if (key.startsWith(prefix)) {
      tokens[key as ColorWithPrefix<Prefix>] =
        value as PrefixedColors<Prefix>[ColorWithPrefix<Prefix>];
    }
  });

  return tokens;
}

export const lookupColor = (color: Color): ColorValue | undefined => {
  const camelCased = camelCase(color) as Color;

  return Colors[camelCased] ?? Colors[color as Color];
};

// Color grouping ( do not re-order )
export const themeColors = [
  Colors.primary,
  Colors.secondary,
  Colors.accent,
] as const;

// Media
export const mediumColors = {
  magazine: Colors.magazine,
  online: Colors.online,
  podcast: Colors.podcast,
  print: Colors.print,
  radio: Colors.radio,
  social: Colors.social,
  tv: Colors.tv,
} as const;

export const sentimentColors = getTokensByPrefix("sentiment-");

// Default Charting Colors ( do not re-order )
export const chartingColors = [
  "#42AAFF",
  "#EA2E96",
  "#D2ABF7",
  "#FAAE14",
  "#4BA05D",
  "#1D39C3",
  "#FFD6E7",
  "#13C3C3",
  "#B8E6FF",
  "#484C7F",
  "#9254DE",
  "#B9BBCF",
] as const;

// Auto Charting Colors ( do not re-order )
export const autoChartingColors = [
  Colors["ds-purple-4"],
  Colors["ds-cyan-5"],
  Colors["daybreak-blue"],
  "#9DF3B0",
  Colors["priority-medium"],
  "#F759AB",
  "#FA6F19",
  "#9254DE",
  "#08979C",
  "#55BD6C",
  "#FFE0BD",
  "#FF9143",
  "#FFD6E7",
  "#FF85C0",
  Colors["ds-purple-2"],
  "#BAE7FF",
  "#69C0FF",
  "#B5F5EC",
  Colors["ds-cyan-4"],
  "#85A5FF",
  "#597EF7",
  "#C3F7CF",
  "#70D687",
  "#FFCCC7",
  Colors["ds-red-4"],
] as const;

export const menuColors = getTokensByPrefix("menu-");

export const autoChartColour = (index: number) =>
  autoChartingColors[index % autoChartingColors.length];

export const trendColors = {
  east: "warning",
  north_east: "ds-cyan-4",
  south_east: "ds-red-5",
} as const;

export default Colors;
